import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import {
  fetchPriorityRankings,
  autoAssignPriorities,
  reorderPriorities,
  updateEpicPriority,
  fetchEpics,
} from '@/store/slices/epicsSlice';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import { ArrowUp, ArrowDown, GripVertical } from 'lucide-react';

interface EpicPriorityManagerProps {
  investibleStructureId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface PriorityFactors {
  businessOutcomeWeight: number;
  requestedCapacityWeight: number;
  featureCountWeight: number;
  statusWeight: number;
}

const DEFAULT_FACTORS: PriorityFactors = {
  businessOutcomeWeight: 0.4,
  requestedCapacityWeight: 0.3,
  featureCountWeight: 0.2,
  statusWeight: 0.1,
};

export const EpicPriorityManager: React.FC<EpicPriorityManagerProps> = ({
  investibleStructureId,
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { priorityRankings, isPrioritizing, error } = useSelector((state: RootState) => state.epics);
  
  const [showScores, setShowScores] = useState(false);
  const [showFactorsModal, setShowFactorsModal] = useState(false);
  const [factors, setFactors] = useState<PriorityFactors>(DEFAULT_FACTORS);
  const [localRankings, setLocalRankings] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen && investibleStructureId) {
      dispatch(fetchPriorityRankings({ isId: investibleStructureId, includeScores: showScores }) as any);
    }
  }, [dispatch, investibleStructureId, isOpen, showScores]);

  useEffect(() => {
    setLocalRankings(priorityRankings);
  }, [priorityRankings]);

  const handleAutoAssign = async () => {
    try {
      await dispatch(autoAssignPriorities({ isId: investibleStructureId, factors }) as any);
      // Refresh rankings
      dispatch(fetchPriorityRankings({ isId: investibleStructureId, includeScores: showScores }) as any);
      // Refresh main EPICs list
      dispatch(fetchEpics({ investibleStructureId }) as any);
    } catch (error) {
      console.error('Failed to auto-assign priorities:', error);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    const items = Array.from(localRankings);
    const [item] = items.splice(index, 1);
    items.splice(index - 1, 0, item);

    setLocalRankings(items);

    try {
      const epicIds = items.map(item => item.id);
      await dispatch(reorderPriorities({ isId: investibleStructureId, epicIds }) as any);
      dispatch(fetchPriorityRankings({ isId: investibleStructureId, includeScores: showScores }) as any);
      dispatch(fetchEpics({ investibleStructureId }) as any);
    } catch (error) {
      setLocalRankings(priorityRankings);
      console.error('Failed to reorder priorities:', error);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === localRankings.length - 1) return;

    const items = Array.from(localRankings);
    const [item] = items.splice(index, 1);
    items.splice(index + 1, 0, item);

    setLocalRankings(items);

    try {
      const epicIds = items.map(item => item.id);
      await dispatch(reorderPriorities({ isId: investibleStructureId, epicIds }) as any);
      dispatch(fetchPriorityRankings({ isId: investibleStructureId, includeScores: showScores }) as any);
      dispatch(fetchEpics({ investibleStructureId }) as any);
    } catch (error) {
      setLocalRankings(priorityRankings);
      console.error('Failed to reorder priorities:', error);
    }
  };

  const handleManualPriorityChange = async (epicId: string, newPriority: number) => {
    if (newPriority < 1) return;

    try {
      await dispatch(updateEpicPriority({ id: epicId, priority: newPriority }) as any);
      // Refresh rankings and main list
      dispatch(fetchPriorityRankings({ isId: investibleStructureId, includeScores: showScores }) as any);
      dispatch(fetchEpics({ investibleStructureId }) as any);
    } catch (error) {
      console.error('Failed to update priority:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      DRAFT: 'bg-[var(--surface-muted)] text-[var(--muted)]',
      SUBMITTED: 'bg-[var(--accent)]/20 text-accent-blue',
      PRIORITIZED: 'bg-status-warning/20 text-status-warning',
      COMMITTED: 'bg-status-success/20 text-status-success',
      STRETCH: 'bg-accent-violet/20 text-accent-violet',
      DELIVERED: 'bg-status-success/20 text-status-success',
      CANCELLED: 'bg-status-error/20 text-status-error',
    };
    return colors[status as keyof typeof colors] || 'bg-[var(--surface-muted)] text-[var(--muted)]';
  };

  const formatScore = (score: number) => {
    return Math.round(score * 10) / 10;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="EPIC Priority Management" size="xl">
      <div className="space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-3">
            <Button
              onClick={handleAutoAssign}
              disabled={isPrioritizing}
              variant="primary"
            >
              {isPrioritizing ? <LoadingSpinner size="sm" /> : null}
              Auto-Assign Priorities
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowFactorsModal(true)}
            >
              Configure Factors
            </Button>
          </div>

          <div className="flex gap-3 items-center">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showScores}
                onChange={(e) => setShowScores(e.target.checked)}
                className="rounded border-[var(--border)] bg-[var(--surface)] text-[var(--accent)] focus:ring-[var(--accent)]"
              />
              <span className="text-sm text-[var(--text)]">Show Scores</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="bg-status-error/10 border border-status-error/30 rounded-md p-3">
            <p className="text-status-error text-sm">{error}</p>
          </div>
        )}

        {/* Priority Rankings */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-[var(--text)]">Priority Rankings</h3>

          {localRankings.length === 0 ? (
            <div className="text-center py-8 text-[var(--muted)]">
              No EPICs found for this Investible Structure
            </div>
          ) : (
            <div className="space-y-2">
              {localRankings.map((epic, index) => (
                <Card key={epic.id} className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Move Controls */}
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0 || isPrioritizing}
                        className="p-1 h-6 w-6"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === localRankings.length - 1 || isPrioritizing}
                        className="p-1 h-6 w-6"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                    </div>

                    {/* Priority Number */}
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg text-accent-blue">
                        #{epic.priority || index + 1}
                      </span>
                      <Input
                        type="number"
                        min="1"
                        value={epic.priority || index + 1}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const newPriority = parseInt(e.target.value);
                          if (!isNaN(newPriority)) {
                            handleManualPriorityChange(epic.id, newPriority);
                          }
                        }}
                        className="w-16 text-center"
                        disabled={isPrioritizing}
                      />
                    </div>

                    {/* EPIC Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold truncate text-[var(--text)]">{epic.name}</h4>
                        <Badge className={getStatusColor(epic.status)}>
                          {epic.status}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-[var(--muted)]">
                        <span>{epic.requestedTeamWeeks} TW requested</span>
                        <span>{epic._count?.features || 0} features</span>
                        <span>{epic._count?.objectives || 0} objectives</span>
                        {epic.program && (
                          <span>Program: {epic.program.name}</span>
                        )}
                      </div>
                    </div>

                    {/* Priority Scores */}
                    {showScores && epic.priorityScore && (
                      <div className="text-right text-sm">
                        <div className="font-semibold text-lg text-[var(--text)]">
                          {formatScore(epic.priorityScore.totalScore)}
                        </div>
                        <div className="space-y-1 text-xs text-[var(--muted)]">
                          <div>Business: {formatScore(epic.priorityScore.businessValue)}</div>
                          <div>Strategic: {formatScore(epic.priorityScore.strategicAlignment)}</div>
                          <div>Risk: {formatScore(epic.priorityScore.riskAdjustment)}</div>
                          <div>Efficiency: {formatScore(epic.priorityScore.resourceEfficiency)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Priority Factors Configuration Modal */}
        <Modal
          isOpen={showFactorsModal}
          onClose={() => setShowFactorsModal(false)}
          title="Priority Calculation Factors"
        >
          <div className="space-y-4">
            <p className="text-sm text-[var(--muted)]">
              Adjust the weights for different factors in priority calculation. All weights should sum to 1.0.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-[var(--text)] mb-1">
                  Business Outcome Weight ({factors.businessOutcomeWeight})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={factors.businessOutcomeWeight}
                  onChange={(e) => setFactors(prev => ({
                    ...prev,
                    businessOutcomeWeight: parseFloat(e.target.value)
                  }))}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--text)] mb-1">
                  Requested Capacity Weight ({factors.requestedCapacityWeight})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={factors.requestedCapacityWeight}
                  onChange={(e) => setFactors(prev => ({
                    ...prev,
                    requestedCapacityWeight: parseFloat(e.target.value)
                  }))}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--text)] mb-1">
                  Feature Count Weight ({factors.featureCountWeight})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={factors.featureCountWeight}
                  onChange={(e) => setFactors(prev => ({
                    ...prev,
                    featureCountWeight: parseFloat(e.target.value)
                  }))}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--text)] mb-1">
                  Status Weight ({factors.statusWeight})
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={factors.statusWeight}
                  onChange={(e) => setFactors(prev => ({
                    ...prev,
                    statusWeight: parseFloat(e.target.value)
                  }))}
                  className="w-full"
                />
              </div>
            </div>
            
            <div className="flex justify-between items-center pt-4 border-t border-[var(--border)]">
              <div className="text-sm text-[var(--text)]">
                Total: {(factors.businessOutcomeWeight + factors.requestedCapacityWeight +
                        factors.featureCountWeight + factors.statusWeight).toFixed(1)}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFactors(DEFAULT_FACTORS);
                  }}
                >
                  Reset
                </Button>
                <Button onClick={() => setShowFactorsModal(false)}>
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </Modal>
  );
};